import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';

export interface BoundingBox {
  top: number;
  bottom: number;
  left: number;
  right: number;
  height: number;
  width: number;
}

export interface PositionPx {
  top: string;
  left: string;
  height: string;
  width: string;
}

export enum PositionX {
  start = 'start',
  center = 'center',
  end = 'end',
}

export enum PositionY {
  above = 'above',
  below = 'below',
}

@Component({
  selector: 'pl-popover',
  templateUrl: './pl-popover.component.html',
})
export class PlPopoverComponent implements OnChanges {
  @Input() public bindingElement!: HTMLElement | BoundingBox;
  @Input() public show!: boolean;
  @Input() public positionX: PositionX = PositionX.start;
  @Input() public positionY: PositionY = PositionY.below;
  @Input() public gap: number = 0;

  @Output() public dismissed: EventEmitter<void> = new EventEmitter<void>();
  @Output() public mouseLeaveParentBoundingBox: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('popover') public popover!: ElementRef;

  private positionLeftAsNumber: WritableSignal<number> = signal(0);
  private positionTopAsNumber: WritableSignal<number> = signal(0);
  public positionLeftAsString: Signal<string> = computed((): string => `${this.positionLeftAsNumber()}px`);
  public positionTopAsString: Signal<string> = computed((): string => `${this.positionTopAsNumber()}px`);

  public menuVisible: 'hidden' | 'visible' = 'hidden';
  public parentBoundingBoxInPx: WritableSignal<PositionPx | null> = signal(null);

  public ngOnChanges(): void {
    this.menuVisible = 'hidden';

    if (this.bindingElement && this.show) {
      setTimeout(async (): Promise<void> => {
        let parentBoundingBox: BoundingBox;
        if (this.bindingElement instanceof HTMLElement) {
          const element: DOMRect = this.bindingElement.getBoundingClientRect();

          parentBoundingBox = {
            top: element.top,
            bottom: element.bottom,
            left: element.left,
            height: element.height,
            width: element.width,
            right: element.right,
          };
        } else {
          parentBoundingBox = this.bindingElement;
        }
        const popoverBoundingBoxBeforeFirstPositioning: DOMRect | null = this.getPopoverBounding();

        this.positionLeftAsNumber.set(
          this.getPopoverLeftPosition(this.positionX, parentBoundingBox, popoverBoundingBoxBeforeFirstPositioning),
        );
        this.positionTopAsNumber.set(
          this.getPopoverTopPosition(this.positionY, parentBoundingBox, popoverBoundingBoxBeforeFirstPositioning),
        );

        await this.rePositionPopoverIfDoesntFitOnScreen(parentBoundingBox, popoverBoundingBoxBeforeFirstPositioning);

        this.menuVisible = 'visible';
        this.parentBoundingBoxInPx.set({
          left: `${parentBoundingBox.left}px`,
          top: `${parentBoundingBox.top}px`,
          width: `${parentBoundingBox.width}px`,
          height: `${parentBoundingBox.height}px`,
        });
        console.log(`\x1b[41m TONIO this.parentBoundingBoxPosition=`, this.parentBoundingBoxInPx(), '\x1b');
      }, 0);
    }
  }

  public onMouseLeaveParentBoundingBoxSimulated(): void {
    console.log('\x1b[42m TONIO PlPopoverComponent onMouseLeaveParentBoundingBoxSimulated =', '\x1b');
    this.mouseLeaveParentBoundingBox.emit();
  }

  public onOverlayClicked(): void {
    console.log('\x1b[42m TONIO PlPopoverComponent onOverlayClicked =', '\x1b');
    this.dismissed.emit();
  }

  public stopPropagation(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  private getPopoverLeftPosition(
    positionX: PositionX,
    parentBoundingBox: BoundingBox,
    popoverBoundingBox: DOMRect | null,
  ): number {
    console.log(
      'TONIO PlPopoverComponent getPopoverLeftPosition positionX=',
      positionX,
      '  parentBoundingBox=',
      parentBoundingBox,
      '  popoverBoundingBox=',
      popoverBoundingBox,
    );
    if (popoverBoundingBox) {
      switch (positionX) {
        case PositionX.start:
          return parentBoundingBox.left;
        case PositionX.center:
          return parentBoundingBox.left + parentBoundingBox.width / 2 - popoverBoundingBox.width / 2;
        case PositionX.end:
          return parentBoundingBox.right - popoverBoundingBox.width;
      }
    }

    return parentBoundingBox.left;
  }

  private getPopoverTopPosition(
    positionY: PositionY,
    parentBoundingBox: BoundingBox,
    popoverBoundingBox: DOMRect | null,
  ): number {
    if (popoverBoundingBox) {
      switch (positionY) {
        case PositionY.above:
          return parentBoundingBox.top - popoverBoundingBox.height - this.gap;
        case PositionY.below:
          return parentBoundingBox.bottom + this.gap;
      }
    }

    return parentBoundingBox.bottom + this.gap;
  }

  private isPopoverNotFullyDisplayedOnScreenForYAxis(popoverBoundingBox: DOMRect | null): boolean {
    if (popoverBoundingBox) {
      const menuOffBottom: boolean =
        popoverBoundingBox.bottom > (window.innerHeight || document.documentElement.clientHeight);

      const menuOffTop: boolean = popoverBoundingBox.top < 0;
      console.log(
        'TONIO PlPopoverComponent isPopoverNotFullyDisplayedOnScreenForYAxis popoverBoundingBox=',
        popoverBoundingBox,
        '  window.innerHeight=',
        window.innerHeight,
        '   document.documentElement.clientHeight=',
        document.documentElement.clientHeight,
      );

      return menuOffBottom || menuOffTop;
    }

    return false;
  }

  private isPopoverNotFullyDisplayedOnScreenForXAxis(popoverBoundingBox: DOMRect | null): boolean {
    if (popoverBoundingBox) {
      const menuOffLeft: boolean = popoverBoundingBox.left < 0;

      const menuOffRight: boolean =
        popoverBoundingBox.right > (window.innerWidth || document.documentElement.clientWidth);
      console.log(
        'TONIO PlPopoverComponent isPopoverNotFullyDisplayedOnScreenForXAxis popoverBoundingBox=',
        popoverBoundingBox,
        '  window.innerHeight=',
        window.innerHeight,
        '   document.documentElement.clientHeight=',
        document.documentElement.clientHeight,
      );

      return menuOffLeft || menuOffRight;
    }

    return false;
  }

  private getAlternativePositionXIfDoesntFitOnScreen(originalPositionX: PositionX): PositionX {
    switch (originalPositionX) {
      case PositionX.start:
        return PositionX.center;
      case PositionX.center:
        return PositionX.end;
      case PositionX.end:
        return PositionX.start;
    }
  }

  private getPopoverBounding(): DOMRect | null {
    if (this.popover && this.popover.nativeElement) {
      return this.popover.nativeElement.getBoundingClientRect();
    }

    return null;
  }

  private rePositionPopoverIfDoesntFitOnScreen(
    parentBoundingBox: BoundingBox,
    popoverBoundingBoxBeforeFirstPositioning: DOMRect | null,
  ): Promise<void> {
    return new Promise((resolve): void => {
      setTimeout(() => {
        console.log(
          `TONIO  this.positionLeft=`,
          this.positionLeftAsNumber(),
          '  this.positionTop=',
          this.positionTopAsNumber(),
          '  show=',
          this.show,
        );
        const popoverBoundingBoxAfterFirstPositioning: DOMRect | null = this.getPopoverBounding();

        const menuOffYAxis: boolean = this.isPopoverNotFullyDisplayedOnScreenForYAxis(
          popoverBoundingBoxAfterFirstPositioning,
        );
        if (menuOffYAxis) {
          this.positionTopAsNumber.set(
            this.getPopoverTopPosition(
              this.positionY === PositionY.below ? PositionY.above : PositionY.below,
              parentBoundingBox,
              popoverBoundingBoxBeforeFirstPositioning,
            ),
          );
        }

        const menuOffXAxis: boolean = this.isPopoverNotFullyDisplayedOnScreenForXAxis(
          popoverBoundingBoxAfterFirstPositioning,
        );
        console.log('TONIO PlPopoverComponent rePositionPopoverIfDoesntFitOnScreen menuOffXAxis=', menuOffXAxis);
        if (menuOffXAxis) {
          const alternativePositionX: PositionX = this.getAlternativePositionXIfDoesntFitOnScreen(this.positionX);
          this.positionLeftAsNumber.set(
            this.getPopoverLeftPosition(
              alternativePositionX,
              parentBoundingBox,
              popoverBoundingBoxBeforeFirstPositioning,
            ),
          );

          setTimeout(() => {
            const popoverBoundingBoxAfterSecondPositioning: DOMRect | null = this.getPopoverBounding();
            const menuOffXAxisAfterRepositioning: boolean = this.isPopoverNotFullyDisplayedOnScreenForXAxis(
              popoverBoundingBoxAfterSecondPositioning,
            );
            console.log(
              'TONIO PlPopoverComponent rePositionPopoverIfDoesntFitOnScreen menuOffXAxisAfterRepositioning=',
              menuOffXAxisAfterRepositioning,
            );
            if (menuOffXAxisAfterRepositioning) {
              const lastTryPositionX: PositionX = this.getAlternativePositionXIfDoesntFitOnScreen(alternativePositionX);
              this.positionLeftAsNumber.set(
                this.getPopoverLeftPosition(
                  lastTryPositionX,
                  parentBoundingBox,
                  popoverBoundingBoxBeforeFirstPositioning,
                ),
              );
            }

            resolve();
          });
        } else {
          resolve();
        }
      });
    });
  }
}
